import { IReactionDisposer, makeAutoObservable, onBecomeObserved, onBecomeUnobserved, reaction, runInAction } from 'mobx';
import { v4 as UUID } from 'uuid';
import { MesoCycleConfiguration, createMesoCycleConfiguration } from './MesoCycleConfiguration';
import { MicroCycle, MicroCycleApi } from './MicroCycle';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { CoachProgramType } from './CoachProgramType';
import { Pageable } from '../../Interfaces/Pageable';

export type MesoCycleQueryRequest = Pageable & {
  athleteSuperMacroId?: string;
};

export type MesoCycle = {
  id: string;
  startDateTime: Date;
  endDateTime?: Date;
  configuration: MesoCycleConfiguration;
  currentMicroCycleId?: string;
  microCycleIds: string[];
  type: CoachProgramType;
  isLastMesoCycle: boolean;
  microCycles?: MicroCycle[];
};

export function createMesoCycle(json?: Partial<MesoCycle>): MesoCycle {
  let disposer: IReactionDisposer | undefined;

  const cycle: MesoCycle = makeAutoObservable({
    id: json?.id ?? UUID(),
    startDateTime: json?.startDateTime ? new Date(json.startDateTime as any) : new Date(),
    endDateTime: json?.endDateTime ? new Date(json.endDateTime as any) : undefined,
    configuration: createMesoCycleConfiguration(json?.configuration as any),
    currentMicroCycleId: json?.currentMicroCycleId,
    microCycleIds: json?.microCycleIds ?? [],
    type: json?.type ?? 'superMacro',
    isLastMesoCycle: json?.isLastMesoCycle ?? false,
    microCycles: undefined as MicroCycle[] | undefined,
  });

  const startFetchMicroCycles = () => {
    disposer?.();
    disposer = reaction(
      () => cycle.microCycleIds.map((m) => m),
      (microCycleIds: string[]) => {
        Promise.all(microCycleIds.map((id) => MicroCycleApi.get(id))).then((microCycles) =>
          runInAction(() => (cycle.microCycles = microCycles)),
        );
      },
      { fireImmediately: true },
    );
  };

  const stopFetchMicroCycles = () => {
    disposer?.();
    disposer = undefined;
  };

  onBecomeObserved(cycle, 'microCycles', startFetchMicroCycles);
  onBecomeUnobserved(cycle, 'microCycles', stopFetchMicroCycles);

  return cycle;
}

export const MesoCycleApi = {
  find(params?: MesoCycleQueryRequest): Promise<MesoCycle[]> {
    return HttpBackend.get(`/coach/meso-cycle`, params).then((result) => result.map((r: any) => createMesoCycle(r)));
  },

  findOne(mesoCycleId: string): Promise<MesoCycle> {
    return HttpBackend.get(`/coach/meso-cycle/${mesoCycleId}`).then((result) => createMesoCycle(result));
  },
};
